import Button from '@pelckmans/elementis/components/button';
import Sidebar from '@pelckmans/elementis/components/sidebar';
import { useBreakpoint } from '@pelckmans/elementis/contexts/BreakpointsContext';
import SidebarIcon from '@pelckmans/elementis/icons/sidebar';
import classNames from 'classnames';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import ContentFooter from '../../../layout/content/ContentFooter';
import Footer from '../../../layout/footer/Footer';
import ApplicationNavigation from '../../../layout/navigation/page-level/ApplicationNavigation';
import PageNavigation from '../../../layout/navigation/page-level/PageNavigation';
import {
  SCROLL_GROW_THRESHOLD,
  SCROLL_SHRINK_THRESHOLD,
} from '../../../shared-components/elementis/hoc/withScrollThreshold';
import SidebarVisibilityContext from '../_contexts/SidebarVisibilityContext';
import SidebarFilters from './browse-mode/SidebarFilters';

const BASE = 'PAGES.CATEGORICAL';

export default function CategoricalMaterialPage() {
  const { materialCategory } = useParams();
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();

  const { isSidebarVisible: sidebarVisible, setSidebarVisibility } = useContext(SidebarVisibilityContext);

  const isSidebarVisible = useMemo(() => {
    if (!materialCategory) return false;

    if (breakpoint === 'desktop') return true;

    return sidebarVisible;
  }, [breakpoint, materialCategory, sidebarVisible]);

  // should be handled in a custom hook for all sidebars with scroll threshold behavior
  const [pastThreshold, setPastThreshold] = useState(false);

  useEffect(() => {
    if (!isSidebarVisible) return;

    const scrollContainer = document.querySelector('.material-page .sidebar-layout');
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (pastThreshold && scrollContainer.scrollTop < SCROLL_SHRINK_THRESHOLD) {
        setPastThreshold(false);
      }
      if (!pastThreshold && scrollContainer.scrollTop > SCROLL_GROW_THRESHOLD) {
        setPastThreshold(true);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [pastThreshold, isSidebarVisible]);

  return (
    <div className={classNames('page', 'material-page', { 'material-page-with-sidebar': materialCategory })}>
      {breakpoint !== 'mobile' && <PageNavigation />}
      <div className="page-content">
        {breakpoint === 'mobile' && <ApplicationNavigation />}
        <div className="main-layout" id="categorical-material">
          <div className="main-layout-container">
            {isSidebarVisible && (
              <div className="sidebar-layout">
                <Sidebar overview={['tablet', 'mobile'].includes(breakpoint)}>
                  <Sidebar.Header
                    headerText={t(`${BASE}.BROWSE_MODE.ASIDE.HEADER_TEXT`)}
                    density={['tablet', 'mobile'].includes(breakpoint) && !pastThreshold ? 'normal' : 'compact'}
                    contextButton={
                      <Button startIcon={<SidebarIcon />} onClick={() => setSidebarVisibility(false)}>
                        {t(`${BASE}.BROWSE_MODE.ASIDE.CONTEXT_BUTTON.CLOSE`)}
                      </Button>
                    }
                  />
                  <Sidebar.Content>
                    <SidebarFilters />
                  </Sidebar.Content>
                  <Sidebar.Footer>
                    {['tablet', 'mobile'].includes(breakpoint) && (
                      <div className="footer-content">
                        <Button
                          prominence="secondary"
                          size="md"
                          className="show-results-button"
                          onClick={() => setSidebarVisibility(false)}
                        >
                          {t(`${BASE}.BROWSE_MODE.ASIDE.FILTERS.SUBMIT_BUTTON.TEXT`)}
                        </Button>
                      </div>
                    )}
                  </Sidebar.Footer>
                </Sidebar>
              </div>
            )}
            <div className="content-layout">
              <main>
                <div className="margin-wrapper">
                  <Outlet />
                  <ContentFooter>
                    <Footer />
                  </ContentFooter>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
